/**
 * filtri — periodo e provenienza per le statistiche e per gli iscritti, e il passaggio dalle
 * risposte di Firestore alla forma di `calcolaStatistiche`. PURO.
 */

import { etichettaProvenienza, type RispostaPerStatistiche } from '@shared/questionari';
import type {
  PlatformLead, PlatformQuestionnaireResponse, PlatformQuestionnaireSessione,
} from '../../../types/platformCrm';
import { millisDi } from '../labels';

export type Periodo = 'tutto' | '7' | '30' | '90';

export const PERIODO_LABELS: Record<Periodo, string> = {
  tutto: 'Sempre',
  '7': 'Ultimi 7 giorni',
  '30': 'Ultimi 30 giorni',
  '90': 'Ultimi 90 giorni',
};

/** Il primo istante del periodo (null = senza limite). */
export function inizioPeriodo(periodo: Periodo, adesso = Date.now()): number | null {
  if (periodo === 'tutto') return null;
  return adesso - Number(periodo) * 24 * 60 * 60 * 1000;
}

export function nelPeriodo(quando: unknown, inizio: number | null): boolean {
  if (inizio === null) return true;
  const ms = millisDi(quando);
  return ms !== null && ms >= inizio;
}

/** La provenienza di una risposta come si conta nelle statistiche. */
export function provenienzaDi(r: Pick<PlatformQuestionnaireResponse, 'provenienza'>): string {
  return etichettaProvenienza(r.provenienza);
}

export function provenienzaSessione(s: Pick<PlatformQuestionnaireSessione, 'provenienza'>): string {
  return etichettaProvenienza(s.provenienza);
}

export function perStatistiche(r: PlatformQuestionnaireResponse): RispostaPerStatistiche {
  return {
    id: r.id,
    stato: r.stato,
    risposte: r.risposte ?? [],
    provenienza: r.provenienza ?? null,
    completato_il: millisDi(r.completato_at) ?? 0,
    istantanea_domande: r.istantanea_domande,
    consenso_richiesto: !!r.consenso,
  };
}

/** Lo stato del consenso agli aggiornamenti di un lead ('nessuno' se non l'ha mai chiesto). */
export function statoConsenso(lead: PlatformLead | undefined): 'nessuno' | 'da_confermare' | 'confermato' | 'revocato' {
  return lead?.consenso_aggiornamenti?.stato ?? 'nessuno';
}

/** I dati di contatto di chi ha risposto: prima la risposta, poi la scheda del lead, poi l'anagrafica storica. */
export function contattiDi(r: PlatformQuestionnaireResponse, lead: PlatformLead | undefined) {
  const c = r.contatti ?? {};
  const a = r.anagrafica ?? null;
  return {
    nome: c.nome ?? lead?.referente_nome ?? '',
    ruolo: c.ruolo ?? lead?.referente_ruolo ?? '',
    azienda: c.azienda ?? a?.ragione_sociale ?? lead?.ragione_sociale ?? '',
    email: c.email ?? a?.email ?? lead?.email ?? '',
    telefono: c.telefono ?? a?.telefono ?? lead?.telefono ?? '',
    partita_iva: c.partita_iva ?? a?.partita_iva ?? lead?.partita_iva ?? '',
    citta: c.citta ?? a?.citta ?? lead?.citta ?? '',
    provincia: c.provincia ?? a?.provincia ?? lead?.provincia ?? '',
  };
}
